import { z } from 'zod';
import { optionalUuidString } from '../zod-helpers/index.js';
import { MANUFACTURING_ORDER_CONSTRAINTS, MAX_MANUFACTURING_ORDER_LINES, MAX_MANUFACTURING_ORDER_LINES_MESSAGE, } from './constraints.js';
import { MANUFACTURING_QUANTITY_MESSAGE, parseManufacturingQuantityText } from './quantity-text.js';
// A line declares only what it builds. How it is cut into work orders is answered at release,
// where the planner can also date each one -- see `lineSplitsSchema`.
export const manufacturingOrderLineFormFieldsSchema = z.object({
    partId: optionalUuidString('Invalid part selection.'),
    // Mirrors `manufacturingOrderLineInputSchema`: the server requires and bounds this, so leaving
    // it unchecked here rejects the whole submit after the round trip instead of naming the field.
    name: z
        .string()
        .trim()
        .min(1, `${MANUFACTURING_ORDER_CONSTRAINTS.name.label} is required.`)
        .max(MANUFACTURING_ORDER_CONSTRAINTS.name.maxLength, `${MANUFACTURING_ORDER_CONSTRAINTS.name.label} must be ${MANUFACTURING_ORDER_CONSTRAINTS.name.maxLength} characters or fewer.`),
    quantity: z.string().trim(),
    // Empty is the unanswered control, which the mapper sends as no line priority at all.
    priorityId: z.string().trim(),
});
export const manufacturingOrderCreateFormFieldsSchema = z.object({
    brandId: optionalUuidString('Invalid brand selection.'),
    locationGroupId: optionalUuidString('Invalid warehouse selection.'),
    dateScheduled: z.string().trim(),
    note: z
        .string()
        .trim()
        .max(MANUFACTURING_ORDER_CONSTRAINTS.note.maxLength, `${MANUFACTURING_ORDER_CONSTRAINTS.note.label} must be ${MANUFACTURING_ORDER_CONSTRAINTS.note.maxLength} characters or fewer.`),
    lines: z
        .array(manufacturingOrderLineFormFieldsSchema)
        .max(MAX_MANUFACTURING_ORDER_LINES, MAX_MANUFACTURING_ORDER_LINES_MESSAGE),
});
export const manufacturingOrderCreateFormSchema = manufacturingOrderCreateFormFieldsSchema.superRefine((values, context) => {
    if (!values.brandId) {
        context.addIssue({
            code: 'custom',
            message: `${MANUFACTURING_ORDER_CONSTRAINTS.brand.label} is required.`,
            path: ['brandId'],
        });
    }
    if (!values.locationGroupId) {
        context.addIssue({
            code: 'custom',
            message: `${MANUFACTURING_ORDER_CONSTRAINTS.locationGroup.label} is required.`,
            path: ['locationGroupId'],
        });
    }
    // A job with no scheduled date is legal: the column is nullable and the floor raises work
    // before it is dated. Release is where each work order gets its own window.
    if (values.lines.length === 0) {
        context.addIssue({
            code: 'custom',
            message: 'Add at least one part line.',
            path: ['lines'],
        });
        return;
    }
    values.lines.forEach((line, lineIndex) => {
        if (!line.partId) {
            context.addIssue({
                code: 'custom',
                message: `${MANUFACTURING_ORDER_CONSTRAINTS.part.label} is required.`,
                path: ['lines', lineIndex, 'partId'],
            });
        }
        if (parseManufacturingQuantityText(line.quantity) === undefined) {
            context.addIssue({
                code: 'custom',
                message: MANUFACTURING_QUANTITY_MESSAGE,
                path: ['lines', lineIndex, 'quantity'],
            });
        }
    });
});
//# sourceMappingURL=create-form.js.map